import CalendarMonth from "@mui/icons-material/CalendarMonth";
import CalendarToday from "@mui/icons-material/CalendarToday";
import Notifications from "@mui/icons-material/Notifications";
import NotificationsOff from "@mui/icons-material/NotificationsOff";
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  SvgIcon,
  Tooltip,
} from "@mui/material";
import { NotificationDigestCadence } from "generated-graphql/graphql";
import { useId, useState, type MouseEvent, type ReactNode } from "react";

import { EMAIL_CADENCE_OFF, type EmailCadenceChoice } from "./useNotificationPreferenceDraft";

/** How often one alert emails: five positions in the column a switch used to hold. */

/**
 * A calendar with a numeral, for daily. The icon set has no numbered calendar, and the
 * numeral is what separates this from the weekly glyph at 20px.
 */
function DailyCalendarIcon() {
  return (
    <SvgIcon viewBox="0 0 24 24" fontSize="small">
      <path
        d="M7 2v2H5.5A2.5 2.5 0 0 0 3 6.5v13A2.5 2.5 0 0 0 5.5 22h13a2.5 2.5 0 0 0 2.5-2.5v-13A2.5 2.5 0 0 0 18.5 4H17V2h-2v2H9V2H7zm-1.5 6h13a.5.5 0 0 1 .5.5V19.5a.5.5 0 0 1-.5.5h-13a.5.5 0 0 1-.5-.5V8.5a.5.5 0 0 1 .5-.5z"
        fillRule="evenodd"
      />
      <text
        x="12"
        y="17.5"
        textAnchor="middle"
        fontSize="9.5"
        fontWeight="700"
        fill="currentColor"
        stroke="none"
      >
        1
      </text>
    </SvgIcon>
  );
}

type CadenceOption = {
  value: EmailCadenceChoice;
  label: string;
  description: string;
  icon: ReactNode;
};

/** Most mail first, off last. "Immediately" is hourly, which the description says. */
const CADENCE_OPTIONS: readonly CadenceOption[] = [
  {
    value: NotificationDigestCadence.Immediate,
    label: "Immediately",
    description: "As soon as we can, usually within the hour",
    icon: <Notifications fontSize="small" />,
  },
  {
    value: NotificationDigestCadence.Daily,
    label: "Daily",
    description: "Once each morning",
    icon: <DailyCalendarIcon />,
  },
  {
    value: NotificationDigestCadence.Weekly,
    label: "Weekly",
    description: "Monday mornings",
    icon: <CalendarToday fontSize="small" />,
  },
  {
    value: NotificationDigestCadence.Monthly,
    label: "Monthly",
    description: "The first of the month",
    icon: <CalendarMonth fontSize="small" />,
  },
  {
    value: EMAIL_CADENCE_OFF,
    label: "Never",
    description: "No email about this alert",
    icon: <NotificationsOff fontSize="small" />,
  },
];

function optionFor(choice: EmailCadenceChoice): CadenceOption {
  return CADENCE_OPTIONS.find((option) => option.value === choice) ?? CADENCE_OPTIONS[1];
}

export type EmailCadenceControlProps = {
  /** Names the control to a screen reader, e.g. "Task overdue email". */
  label: string;
  /** Why this row is muted from above, or where its position came from. */
  explanation: string;
  value: EmailCadenceChoice;
  disabled: boolean;
  onChange: (choice: EmailCadenceChoice) => void;
};

export function EmailCadenceControl({
  label,
  explanation,
  value,
  disabled,
  onChange,
}: EmailCadenceControlProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const selected = optionFor(value);
  // Unique per instance rather than derived from the label, so two rows sharing a title
  // cannot collide on `aria-describedby`.
  const descriptionId = `${useId()}-cadence-description`;

  const choose = (choice: EmailCadenceChoice) => {
    setAnchorEl(null);
    if (choice !== value) {
      onChange(choice);
    }
  };

  return (
    <>
      <Tooltip title={`${label}: ${selected.label} — ${selected.description}`} arrow>
        {/* Wrapped, because a disabled MUI button fires no pointer events: without the
            span the tooltip disappears for the whole page while a save is in flight,
            and it is the only place the cadence is spelled out in words. */}
        <span>
          {/* The accessible name states the current schedule: for a reader who cannot
              see the glyph, it is the only place their own setting is readable. */}
          <IconButton
            size="small"
            disabled={disabled}
            onClick={(event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)}
            aria-label={`${label}: ${selected.label}`}
            aria-describedby={descriptionId}
            aria-haspopup="menu"
            aria-expanded={anchorEl !== null}
            color={value === EMAIL_CADENCE_OFF ? "default" : "primary"}
            sx={{ color: value === EMAIL_CADENCE_OFF ? "text.disabled" : undefined }}
          >
            {selected.icon}
          </IconButton>
        </span>
      </Tooltip>
      <span id={descriptionId} hidden>
        {explanation}
      </span>

      <Menu
        anchorEl={anchorEl}
        open={anchorEl !== null}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{ "aria-label": `How often ${label.toLowerCase()} arrives` }}
      >
        {CADENCE_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === value}
            onClick={() => choose(option.value)}
          >
            <ListItemIcon>{option.icon}</ListItemIcon>
            <ListItemText primary={option.label} secondary={option.description} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
